import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/api.service';
import { PopupService } from 'src/app/services/popup.service';
import {
  actionsForTarget,
  isPoiSensor,
  minActionCredits,
  normalizeCreditActions,
  VoyageCreditAction,
} from 'src/app/services/operators/voyageradar-credit-actions';
import { CollectionPopupComponent } from '../../../shared';
import {
  canRequestVoyageRefund,
  canSubmitVoyageRefund,
  hasVoyageToiFinished,
  voyageCreditsRemaining,
} from '../voyage-credits.model';

/**
 * Voyage Radar credit usage + refund, in one dialog.
 *
 * Replaces two things: the inline "Credit usage" panel that sat below Vessel
 * Tracks, and Voyage Radar's use of the shared ReturnRefundComponent. Those
 * were the same subject split across two places — a ledger the user had to
 * expand, and a refund form that then restated the numbers. Opening one dialog
 * from the refund icon puts the balance and the action on it together.
 *
 * Everything is derived from the wallet pair already on the sensor
 * (`apiCredits` / `apiCreditsRemaining`). No request is made to render it.
 *
 * The wallet is the ONLY spend record there is. This dialog used to break the
 * total into "refreshes used" and "tracks used" by multiplying the two logbooks
 * (`refreshHistory` / `trackHistory`) by their catalogue prices, guarded by a
 * reconciliation check. Both are gone: those logbooks record what was DRAWN,
 * not what was charged, nothing audits them against the debits, and a
 * breakdown that happens to add up is still not a measurement. A refund
 * dialog is the wrong place to show a number no system of record backs, so it
 * reports purchased, used and remaining — all three straight off the wallet.
 */
@Component({
  selector: 'app-voyage-credit-refund',
  templateUrl: './voyage-credit-refund.component.html',
  styleUrls: ['./voyage-credit-refund.component.scss'],
})
export class VoyageCreditRefundComponent implements OnInit {
  formGroup: FormGroup;

  private reasonControl: FormControl;

  readonly minMsgLength = 5;
  readonly maxMsgLength = 500;

  /** Guards against a double submit while the request is in flight. */
  submitting = false;

  constructor(
    private dialogRef: MatDialogRef<VoyageCreditRefundComponent>,
    private apiService: ApiService,
    private popupService: PopupService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.reasonControl = new FormControl(
      'I would like to request the remainder of the credits to be refunded.',
      [
        Validators.required,
        Validators.minLength(this.minMsgLength),
        Validators.maxLength(this.maxMsgLength),
      ],
    );
    this.formGroup = new FormGroup({ reason: this.reasonControl });
  }

  get sensor(): any {
    return this.data?.sensor;
  }

  get reason(): FormControl {
    return this.reasonControl;
  }

  get reasonLength(): number {
    return `${this.reasonControl?.value ?? ''}`.length;
  }

  // ── the catalogue ─────────────────────────────────────────────────────────
  /**
   * Narrowed to what this task could actually spend on, so the stranded-wallet
   * line below is measured against reachable actions only — the area refresh
   * was never available to a point-tasked sensor. That one row is all the
   * narrowing still removes, and only in that direction: the nearby entry
   * carries no target because it prices an endpoint that is due to answer a
   * search around a selected vessel as well as a point refresh — plan steps
   * 4-8, not built yet — so an area-tasked sensor keeps that row and is
   * measured against it too.
   */
  get actions(): VoyageCreditAction[] {
    return actionsForTarget(
      normalizeCreditActions(this.sensor?.apiCreditActions),
      isPoiSensor(this.sensor),
    );
  }

  // Per-action prices are deliberately NOT read here any more. The dialog no
  // longer prices anything — `actions` survives only for the stranded-wallet
  // floor below, which asks whether ANY action is still affordable rather than
  // what a past one cost.

  // ── the wallet ────────────────────────────────────────────────────────────
  get purchased(): number {
    const n = Number(this.sensor?.apiCredits);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  }

  /** Null means UNKNOWN — never render it as empty. */
  get remaining(): number | null {
    return voyageCreditsRemaining(this.sensor);
  }

  get used(): number {
    const left = this.remaining;
    return left == null ? 0 : Math.max(0, this.purchased - left);
  }

  /** The bar's single segment, as a percentage of the purchase. */
  get usedPct(): number {
    return this.purchased ? Math.min(100, (this.used * 100) / this.purchased) : 0;
  }

  // ── money ─────────────────────────────────────────────────────────────────
  get unitCost(): number {
    return Number(this.sensor?.unitCost) || 0;
  }

  get showMoney(): boolean {
    return this.unitCost > 0;
  }

  get estimatedRefund(): number {
    return (this.remaining ?? 0) * this.unitCost;
  }

  // ── state ─────────────────────────────────────────────────────────────────
  /** A non-zero balance too small for any action — it can still be refunded. */
  get stranded(): boolean {
    const left = this.remaining;
    return left != null && left > 0 && left < minActionCredits(this.actions);
  }

  get allUsed(): boolean {
    return this.remaining === 0;
  }

  get refundApproved(): boolean {
    return !!this.sensor?.returnRefundAccept;
  }

  /**
   * Credits returned by an approved refund.
   *
   * Approval zeroes the wallet, so `apiCreditsRemaining` is 0 by the time this
   * dialog can show an approved state — without the balance captured at
   * approval there would be nothing left to report but a zero.
   */
  get refundedCredits(): number {
    const n = Number(this.sensor?.refundedApiCredits);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  }

  get refundedValue(): number {
    return this.refundedCredits * this.unitCost;
  }

  get refundDeclined(): boolean {
    return !!this.sensor?.returnRefundReject;
  }

  get refundPending(): boolean {
    return !!this.sensor?.refundRequest && !this.refundApproved && !this.refundDeclined;
  }

  get toiFinished(): boolean {
    return hasVoyageToiFinished(this.sensor);
  }

  /**
   * Whether the request FORM renders at all — credits left and no terminal
   * refund state. TOI is deliberately not part of this: usage stays viewable
   * even before the TOI finishes, only submitting is blocked until then.
   */
  get canRequestRefund(): boolean {
    return canRequestVoyageRefund(this.sensor);
  }

  /** Gates the "Request refund" button itself: users may only apply once the TOI is done. */
  get canSubmitRefund(): boolean {
    return canSubmitVoyageRefund(this.sensor);
  }

  get errorMessage(): string {
    const c = this.reasonControl;
    if (!c || c.valid) return '';
    if (c.hasError('required')) return 'Cannot be empty';
    if (c.hasError('minlength')) return `Must be at least ${this.minMsgLength} characters`;
    if (c.hasError('maxlength')) return `Must be no more than ${this.maxMsgLength} characters`;
    return 'Must be valid';
  }

  // ── actions ───────────────────────────────────────────────────────────────
  onCancel(): void {
    this.dialogRef.close('close');
  }

  onSubmit(): void {
    if (!this.canSubmitRefund || this.formGroup.invalid || this.submitting) return;
    this.submitting = true;

    this.apiService
      .returnRefundRequest({
        taskId: this.data?.taskId,
        sensorId: this.data?.sensorId,
        sceneId: undefined,
        refundRequest: true,
        refundRequestReason: this.reasonControl.value,
        product: 'VoyageRadar',
        action: 'voyage-credits',
        feedId: undefined,
      })
      .subscribe(
        (res: any) => {
          if (res?.msg === 'Success') {
            this.dialogRef.close({ refundRequested: true });
            return;
          }
          this.submitting = false;
          this.popupService.openPopup(
            CollectionPopupComponent,
            'An error occurred during processing and ingestion of the request. Please try after sometime.',
            '',
            '40%',
          );
        },
        () => {
          // Re-enable rather than leaving a dead button: nothing was charged,
          // and the request is safe to retry.
          this.submitting = false;
          this.popupService.openPopup(
            CollectionPopupComponent,
            'An error occurred during processing and ingestion of the request. Please try after sometime.',
            '',
            '40%',
          );
        },
      );
  }
}
